import React from 'react';

import { useIntl } from 'react-intl-next';

import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import PremiumIcon from '@atlaskit/icon/core/premium';
import { ConfluenceLogo } from '@atlaskit/logo';
import { Modal } from '@atlassian/growth-pattern-library-modal';

import { ScreenEvent, useAnalytics } from '../../utils/analytics';
import {
	type CommonAnalyticsAttributes,
	CONFLUENCE_AFTER_STANDARD_UPGRADE_AI_MODAL,
	type ModalDismissalMethod,
	type ModalShownPreOrPostUpgrade,
	type OfferShownType,
	type SupportedProduct,
} from '../../utils/types';
import { useModalState } from '../../utils/useModalState';
import { StandardOfferAIModalRightSide } from '../ConfluenceStandardOfferAIModal';
import { EditionHighlightsLeftContent } from '../EditionHighlightsLeftContent';
import { PrimaryAndSecondaryCTAActionsGroup } from '../PrimaryAndSecondaryCTAActionsGroup';

import { messages } from './messages';

export const ConfluenceAfterStandardUpgradeAIModal = ({
	onInteractionComplete,
}: {
	onInteractionComplete: () => void;
}): React.JSX.Element | null => {
	const intl = useIntl();

	const { isOpen, handleClose } = useModalState();

	const componentAttributes: CommonAnalyticsAttributes = {
		product: 'confluence' as SupportedProduct,
		offerShown: 'AI' as OfferShownType,
		modalShownPrePostUpgrade: 'POST_UPGRADE' as ModalShownPreOrPostUpgrade,
	};

	const { clickedDismissModal, clickedPrimaryCTAOnModal } = useAnalytics();

	const handlePrimaryCTAClick = (e?: React.MouseEvent<HTMLAnchorElement>) => {
		e?.preventDefault();
		clickedPrimaryCTAOnModal(CONFLUENCE_AFTER_STANDARD_UPGRADE_AI_MODAL, componentAttributes);
		handleClose();
		onInteractionComplete();
	};

	const handleDismissModal = (dismissalMethod: ModalDismissalMethod) => {
		clickedDismissModal(CONFLUENCE_AFTER_STANDARD_UPGRADE_AI_MODAL, {
			...componentAttributes,
			dismissalMethod,
		});
		handleClose();
		onInteractionComplete();
	};

	if (!isOpen) {
		return null;
	}

	return (
		<Modal
			height="560px"
			logo={<ConfluenceLogo shouldUseNewLogoDesign size="xsmall" />}
			title={intl.formatMessage(messages.confluenceAfterUpgradeAIStandardModalTitle)}
			subtitle={intl.formatMessage(messages.confluenceAfterUpgradeAIStandardModalSubtitle)}
			testId="growth-pattern-library-modal"
			includeCloseButton
			onClose={() => {
				handleDismissModal('closeButton');
			}}
			sideContent={<StandardOfferAIModalRightSide />}
			actions={
				<PrimaryAndSecondaryCTAActionsGroup
					onPrimaryCTAClick={handlePrimaryCTAClick}
					onSecondaryCTAClick={() => handleDismissModal('secondaryCTAButton')}
					primaryCTALabel={intl.formatMessage(
						messages.confluenceAfterUpgradeAIStandardModalPrimaryCTA,
					)}
					secondaryCTALabel={intl.formatMessage(
						messages.confluenceAfterUpgradeAIStandardModalSecondaryCTA,
					)}
				/>
			}
		>
			<EditionHighlightsLeftContent
				highlights={[
					{
						icon: AiGenerativeTextIcon,
						title: intl.formatMessage(messages.confluenceAfterUpgradeAIStandardModalAIDraft),
					},
					{
						icon: AiGenerativeTextSummaryIcon,
						title: intl.formatMessage(messages.confluenceAfterUpgradeAIStandardModalAISummaries),
					},
					{
						icon: PremiumIcon,
						title: intl.formatMessage(
							messages.confluenceAfterUpgradeAIStandardModalAIContextualInfo,
						),
					},
				]}
			/>
			<ScreenEvent name="upgradeFreePoolTargetExperimentModal" attributes={componentAttributes} />
		</Modal>
	);
};
